import React, { useEffect, useState } from 'react'

import {
  SubaccountStats,
  SubscriptionStats
} from '../../api/services/dd_panel_main_accounts/types.ts'
import Breadcrumbs from '../../components/common/Breadcrumbs/Breadcrumbs.tsx'
import Card from '../../components/common/Card/Card.tsx'
import Loader from '../../components/common/Loader/Loader.tsx'
import Table from '../../components/common/Table/Table.tsx'
import { GlobalStatisticsCharts } from '../../components/payments/StatisticsCharts.tsx'
import StatisticsDatesChooser from '../../components/payments/StatisticsDatesChooser.tsx'
import {
  useGlobalStatistics,
  useSubaccountsSalesStatistics
} from '../../hooks/useStatistics.ts'
import { getCurrentMonthBoundaries } from '../../utils/dates.ts'

const Statistics = () => {
  const { firstDay, lastDay } = getCurrentMonthBoundaries()
  const [fromDate, setFromDate] = useState(firstDay)
  const [toDate, setToDate] = useState(lastDay)

  const {
    data: globalData,
    loading,
    refetch
  } = useGlobalStatistics(fromDate, toDate)
  const { data: subaccountsData, refetch: subaccountsRefetch } =
    useSubaccountsSalesStatistics(fromDate, toDate)

  useEffect(() => {
    if (fromDate !== firstDay || toDate !== lastDay) {
      refetch()
      subaccountsRefetch()
    }
  }, [fromDate, toDate])

  let subscriptionStats: SubscriptionStats[] = [
    {
      subscription_length: 1,
      orders_count: 0,
      orders_paid_count: 0,
      paid_amount: 0
    },
    {
      subscription_length: 3,
      orders_count: 0,
      orders_paid_count: 0,
      paid_amount: 0
    },
    {
      subscription_length: 6,
      orders_count: 0,
      orders_paid_count: 0,
      paid_amount: 0
    },
    {
      subscription_length: 12,
      orders_count: 0,
      orders_paid_count: 0,
      paid_amount: 0
    }
  ]

  if (globalData) {
    subscriptionStats = subscriptionStats.map((s) => {
      const found = globalData.subscription_stats.find(
        (a) => a.subscription_length === s.subscription_length
      )
      return found ? { ...s, ...found } : s
    })
  }

  return (
    <>
      <Breadcrumbs pageName="Statystyki" breadcrumbs={[]} />

      <Card>
        <Card.Body>
          <StatisticsDatesChooser
            fromDate={fromDate}
            toDate={toDate}
            setFromDate={setFromDate}
            setToDate={setToDate}
            lastDay={lastDay}
          />
          {loading && <Loader label={'Pobieranie danych o saldzie...'} />}

          {globalData && (
            <div className="flex flex-col justify-between mb-12 border-b-4 dark:border-b-gray-700 pb-6">
              <p className="text-2xl font-bold mt-4">Podsumowanie</p>
              <div className="flex flex-col md:flex-row items-start md:gap-10">
                <div className="my-6 text-md">
                  <p className="">
                    Liczba wszystkich zamówień:{' '}
                    <span className="font-bold">
                      {globalData.global_stats.total_orders_count}
                    </span>
                  </p>
                  <p className="">
                    Liczba opłaconych zamówień:{' '}
                    <span className="font-bold">
                      {globalData.global_stats.total_paid_orders_count}
                    </span>
                  </p>
                  <p className="text-[14px] px-4">
                    <li>w tym z wykorzystaniem darmowego vouchera:{' '}
                    <span className="font-bold">
                      {globalData.global_stats.orders_paid_with_voucher_count}
                    </span>
                    </li>
                  </p>
                  <p className="mt-2">
                    Wartość opłaconych zamówień:{' '}
                    <span className="font-bold">
                      {globalData.global_stats.total_paid_amount
                        ? globalData.global_stats.total_paid_amount.toFixed(2)
                        : '0.00'}
                    </span>
                  </p>
                </div>
                <div className="my-6 text-md">
                  <p className="">
                    Liczba nowych subskrypcji:{' '}
                    <span className="font-bold">
                      {globalData.global_stats.new_subscriptions}
                    </span>
                  </p>
                  <p className="">
                    Liczba odnawianych subskrypcji:{' '}
                    <span className="font-bold">
                      {globalData.global_stats.renewed_subscriptions}
                    </span>
                  </p>
                </div>
              </div>

              {globalData?.subscription_stats && (
                <>
                  <GlobalStatisticsCharts data={subscriptionStats} />
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                    {subscriptionStats.map((s) => (
                      <div
                        key={s.subscription_length}
                        className="p-4 rounded border shadow bg-gray-100 dark:bg-gray-800"
                      >
                        <div className="font-bold">
                          {s.subscription_length} m
                        </div>
                        <div>Liczba zamówień: {s.orders_count}</div>
                        <div>W tym opłacone : {s.orders_paid_count}</div>
                        <div>Suma opłat: {s.paid_amount.toFixed(2)} zł</div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {subaccountsData && (
            <div className="flex flex-col justify-between mb-12 border-b-4 dark:border-b-gray-700 pb-6">
              <p className="text-2xl my-4">Podział na konta podrzędne</p>

              <Table
                key={JSON.stringify(subaccountsData)}
                columns={[
                  {
                    header: 'E-mail konta',
                    accessor: 'assigned_user__dd_panel_account__user__email',
                    render: (row: SubaccountStats) => (
                      <>{row.assigned_user__dd_panel_account__user__email}</>
                    )
                  },
                  {
                    header: 'Liczba zamówień',
                    accessor: 'order_count',
                    classNameHeaderFirstLine: 'text-center',
                    classNameHeaderSecondLine:
                      'text-center text-[14px] text-gray-500',
                    classNameTd: 'text-center',
                    className: 'text-center items-center',
                    render: (row: SubaccountStats) => <>{row.order_count}</>
                  },
                  {
                    header: 'Zamówienia opłacone',
                    accessor: 'orders_paid',
                    headerSecondLine: '( wszystkie / w tym darmowe )',
                    classNameHeaderFirstLine: 'text-center',
                    classNameHeaderSecondLine:
                      'text-center text-[14px] text-gray-500',
                    classNameTd: 'text-center',
                    className: 'text-center items-center',
                    render: (row: SubaccountStats) => (
                      <>
                        {row.orders_paid_count}{' '}
                        / {row.orders_paid_with_voucher_count}
                      </>
                    )
                  },
                  {
                    header: 'Liczba Subskrypcji',
                    accessor:
                      'new_subscription_orders_count__renewed_subscription_orders_count',
                    headerSecondLine: '(nowe / odnowione)',
                    classNameHeaderFirstLine: 'text-center',
                    classNameHeaderSecondLine:
                      'text-center text-[14px] text-gray-500',
                    classNameTd: 'text-center',
                    className: 'text-center items-center',
                    render: (row: SubaccountStats) => (
                      <>
                        {row.new_subscription_orders_count} /{' '}
                        {row.renewed_subscription_orders_count}
                      </>
                    )
                  },
                  {
                    header: 'Suma opłat',
                    accessor: 'paid_amount',
                    classNameTd: 'text-center',
                    className: 'text-center items-center',
                    render: (row: SubaccountStats) => (
                      <>
                        {row.paid_amount ? row.paid_amount.toFixed(2) : '0.00'}
                      </>
                    )
                  }
                ]}
                data={subaccountsData}
              />
            </div>
          )}
        </Card.Body>
      </Card>
    </>
  )
}
export default Statistics
